<template>
  <div class="pc-records-page">
    <!-- 顶部导航 -->
    <div class="page-header1">
      <button class="back-btn" @click="goBack">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M15 17L8 10L15 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {{ $t('_sp._fanh') }}
      </button>
      <span class="page-title">{{ $t('_ybp._djsjl') }}</span>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-grid">
      <div class="stats-card">
        <div class="stats-card-content">
          <div class="stats-icon total-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="12" width="4" height="10" rx="1" stroke="currentColor" stroke-width="1.5" />
              <rect x="9" y="7" width="4" height="15" rx="1" stroke="currentColor" stroke-width="1.5" />
              <rect x="16" y="2" width="4" height="20" rx="1" stroke="currentColor" stroke-width="1.5" />
            </svg>
          </div>
          <div class="stats-info">
            <div class="stats-label">{{ $t('_ybp._zsy') }}</div>
            <div class="stats-value">$ {{ formatNumber(profit.zsy) }}</div>
          </div>
        </div>
      </div>

      <div class="stats-card">
        <div class="stats-card-content">
          <div class="stats-icon current-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5" />
              <path d="M12 7V12L14.5 14.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </div>
          <div class="stats-info">
            <div class="stats-label">{{ $t('_sy._djs') }}</div>
            <div class="stats-value">$ {{ formatNumber(profit.djs) }}</div>
          </div>
        </div>
      </div>

      <div class="stats-card">
        <div class="stats-card-content">
          <div class="stats-icon settled-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div class="stats-info">
            <div class="stats-label">{{ $t('_sy._yjs') }}</div>
            <div class="stats-value">$ {{ formatNumber(profit.yjs) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 数据表格 -->
    <div class="table-card">
      <div class="table-header">
        <h3 class="table-title">{{ $t('_ybp._djsjl') }}</h3>
        <span class="table-subtitle">{{ $t('_sy._gdstjl', { n: pagination.total }) }}</span>
      </div>

      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th style="width: 60px">#</th>
              <th style="width: 200px">{{ $t('_ybp._bqbm') }}</th>
              <th style="width: 150px">{{ $t('_ybp._je') }}</th>
              <th style="width: 200px">{{ $t('_ybp._sj') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="4" class="loading-cell">
                <div class="loading-spinner"></div>
                {{ $t('_bqq._jzz') }}
              </td>
            </tr>
            <tr v-else-if="recordsList.length === 0">
              <td colspan="4" class="empty-cell">
                <div class="empty-state">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <circle cx="32" cy="32" r="28" stroke="currentColor" stroke-width="1.5" opacity="0.2" />
                    <path d="M24 28L28 24M28 24L32 28M28 24L28 40M40 28L36 24M36 24L32 28M36 24L36 40" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.4" />
                  </svg>
                  <p>{{ $t('_bqq._wz') }}</p>
                </div>
              </td>
            </tr>
            <tr v-for="(item, index) in recordsList" :key="item.code + index">
              <td>{{ (pagination.current - 1) * pagination.pageSize + index + 1 }}</td>
              <td>
                <span class="code-tag">{{ item.code }}</span>
              </td>
              <td class="amount-cell">$ {{ formatNumber(item.target) }}</td>
              <td>{{ item.date }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div class="table-footer" v-if="pagination.total > 0">
        <div class="pagination-info">{{ $t('_sy._gjd', { n: pagination.total }) }}</div>
        <div class="pagination-controls">
          <button class="page-btn" @click="changePage(pagination.current - 1)" :disabled="pagination.current === 1">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: page === pagination.current }" @click="changePage(page)" :disabled="page === '...'">
            {{ page }}
          </button>
          <button class="page-btn" @click="changePage(pagination.current + 1)" :disabled="pagination.current === totalPages">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'

export default {
  name: 'RecordsPage',
  setup() {
    const { proxy } = getCurrentInstance()
    const router = useRouter()

    // 统计数据
    const profit = reactive({
      zsy: 0,
      djs: 0,
      yjs: 0
    })

    // 列表数据
    const recordsList = ref([])
    const loading = ref(false)

    // 分页
    const pagination = reactive({
      current: 1,
      pageSize: 20,
      total: 0
    })

    // 计算总页数
    const totalPages = computed(() => {
      return Math.ceil(pagination.total / pagination.pageSize)
    })

    // 计算可见页码
    const visiblePages = computed(() => {
      const total = totalPages.value
      const current = pagination.current
      const pages = []

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        } else if (current >= total - 3) {
          pages.push(1)
          pages.push('...')
          for (let i = total - 4; i <= total; i++) {
            pages.push(i)
          }
        } else {
          pages.push(1)
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        }
      }
      return pages
    })

    // 返回上一页
    const goBack = () => {
      router.back()
    }

    // 获取收益统计
    const getTodayProfit = () => {
      proxy.$api.profit
        .getToDayProfit()
        .then((res) => {
          if (res.code === 0) {
            profit.zsy = res.data.zsy || 0
            profit.djs = res.data.djs || 0
            profit.yjs = res.data.yjs || 0
          } else {
            proxy.$message?.error(res.msg)
          }
        })
        .catch((err) => {
          console.error('获取统计数据异常:', err)
        })
    }

    // 获取收益列表
    const getRecords = () => {
      if (loading.value) return

      loading.value = true

      proxy.$api.profit
        .getIncomeList({
          page: pagination.current,
          size: pagination.pageSize
        })
        .then((res) => {
          if (res.code === 0) {
            const list = res.data?.data || []

            recordsList.value = list.map((item) => ({
              ...item,
              target: Number(item.target) || 0
            }))

            pagination.total = res.data?.total || list.length
          } else {
            proxy.$message?.error(res.msg)
            recordsList.value = []
            pagination.total = 0
          }
        })
        .catch((err) => {
          console.error('获取列表异常:', err)
          recordsList.value = []
          pagination.total = 0
        })
        .finally(() => {
          loading.value = false
        })
    }

    // 切换页码
    const changePage = (page) => {
      if (page < 1 || page > totalPages.value) return
      if (page === '...') return

      pagination.current = page
      getRecords()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    // 格式化数字
    const formatNumber = (num) => {
      if (num === undefined || num === null) return '0.00'
      return Number(num).toFixed(2)
    }

    onMounted(() => {
      getTodayProfit()
      getRecords()
    })

    return {
      profit,
      recordsList,
      loading,
      pagination,
      totalPages,
      visiblePages,
      formatNumber,
      changePage,
      goBack
    }
  }
}
</script>

<style scoped>
/* ===== 基础重置 ===== */
* {
  box-sizing: border-box;
}

.pc-records-page {
  min-height: 100vh;
  background: #0d1117;
  color: #e6edf3;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  padding: 24px;
}

/* ===== 顶部导航 ===== */
.page-header1 {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #30363d;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #58a6ff;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 6px;
  transition: all 0.2s;
}

.back-btn:hover {
  background: rgba(88, 166, 255, 0.1);
  color: #79c0ff;
}

.back-btn svg {
  flex-shrink: 0;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #e6edf3;
}

/* ===== 滚动条 ===== */
.pc-records-page::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.pc-records-page::-webkit-scrollbar-track {
  background: #0d1117;
}

.pc-records-page::-webkit-scrollbar-thumb {
  background: #30363d;
  border-radius: 3px;
}

.pc-records-page::-webkit-scrollbar-thumb:hover {
  background: #484f58;
}

/* ===== 统计卡片 ===== */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stats-card {
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 8px;
  padding: 20px 24px;
  transition: all 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-2px);
  border-color: #58a6ff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.stats-card-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stats-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.total-icon {
  background: rgba(88, 166, 255, 0.15);
  color: #58a6ff;
}

.current-icon {
  background: rgba(255, 184, 0, 0.15);
  color: #ffb800;
}

.settled-icon {
  background: rgba(35, 134, 54, 0.15);
  color: #238636;
}

.stats-info {
  flex: 1;
  min-width: 0;
}

.stats-label {
  font-size: 13px;
  color: #8b949e;
  margin-bottom: 4px;
}

.stats-value {
  font-size: 24px;
  font-weight: 700;
  color: #e6edf3;
}

/* ===== 表格 ===== */
.table-card {
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #30363d;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #e6edf3;
  margin: 0;
}

.table-subtitle {
  font-size: 14px;
  color: #8b949e;
}

.table-wrapper {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table thead {
  background: #0d1117;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #8b949e;
  border-bottom: 1px solid #30363d;
  white-space: nowrap;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #30363d;
  color: #e6edf3;
}

.data-table tbody tr:hover {
  background: #1c2128;
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.code-tag {
  display: inline-block;
  padding: 2px 10px;
  background: rgba(88, 166, 255, 0.15);
  color: #58a6ff;
  border-radius: 4px;
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 13px;
  border: 1px solid rgba(88, 166, 255, 0.2);
}

.amount-cell {
  font-weight: 600;
  color: #ffb800;
}

/* ===== 加载状态 ===== */
.loading-cell {
  text-align: center !important;
  padding: 40px 0 !important;
  color: #8b949e;
}

.loading-spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 2px solid #30363d;
  border-top-color: #58a6ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== 空状态 ===== */
.empty-cell {
  text-align: center !important;
  padding: 60px 0 !important;
}

.empty-state {
  color: #8b949e;
}

.empty-state svg {
  color: #30363d;
  margin-bottom: 16px;
}

.empty-state p {
  margin: 0;
  font-size: 16px;
}

/* ===== 分页 ===== */
.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-top: 1px solid #30363d;
  flex-wrap: wrap;
  gap: 12px;
  background: #0d1117;
}

.pagination-info {
  font-size: 14px;
  color: #8b949e;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  background: transparent;
  border: 1px solid #30363d;
  border-radius: 6px;
  color: #e6edf3;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled):not(.active) {
  border-color: #58a6ff;
  color: #58a6ff;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-btn.active {
  background: #238636;
  border-color: #238636;
  color: #fff;
}

/* ===== 响应式 ===== */
@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .pc-records-page {
    padding: 12px;
  }

  .page-header1 {
    flex-wrap: wrap;
    gap: 8px;
  }

  .page-title {
    font-size: 17px;
  }

  .stats-grid {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .stats-card {
    padding: 16px;
  }

  .stats-value {
    font-size: 20px;
  }

  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .table-footer {
    flex-direction: column;
    align-items: center;
  }

  .pagination-info {
    text-align: center;
  }

  .pagination-controls {
    flex-wrap: wrap;
    justify-content: center;
  }

  .data-table {
    font-size: 13px;
  }

  .data-table th,
  .data-table td {
    padding: 8px 10px;
  }
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .back-btn span {
    display: none;
  }

  .page-btn {
    min-width: 28px;
    height: 28px;
    font-size: 12px;
  }
}
</style>
